// @ts-check
import { createSelector } from '../utils/selector-utils'
import { createCrudProp } from '../utils/crud-prop'
import { sortByTimeIfExists } from '../utils/sort-by'

const posProps = createCrudProp({
  rootKey: 'pos',
  requiredProps: ['name'],
  sortFn: sortByTimeIfExists,
})

/**
 * @param {string} name The position name
 * @returns {any} The changes to apply to the case.
 */
export const actionAddPosition = name => posProps.actionAdd({ name })

/**
 * @param {string} id The position ID
 * @returns {any} The changes to apply to the case.
 */
export const actionRemovePosition = posProps.actionRemove

/**
 * @param {string} id The position ID
 * @param {any} updates The updates to apply to the position
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdatePosition = posProps.actionUpdate

/**
 * @param {boolean} posSkipped
 * @returns {any} The changes to apply to the case.
 */
export const actionSkipPositions = posSkipped => ({
  posSkipped: posSkipped ? true : null,
})

/** @type {CaseSelector<boolean>} */
export const selectPositionsSkipped = caseData =>
  caseData ? !!caseData.posSkipped : false

/** @type {CaseSelector<CasePositionEntry[]>} */
export const selectPositions = createSelector(
  selectPositionsSkipped,
  posProps.select,
  (skipped, selected) => (skipped ? [] : selected)
)

/** @type {CaseSelector<boolean>} */
export const selectPositionsComplete = createSelector(
  selectPositionsSkipped,
  selectPositions,
  (skipped, positions) => skipped || !!positions.length
)

/** @type {CaseSelector<PositionEvent[]>} */
export const selectPositionEvents = createSelector(selectPositions, positions =>
  positions
    // remove all without a time
    .filter(entry => entry.time)
    .map(
      positionEntry =>
        /** @type {PositionEvent} */
        ({
          id: positionEntry.id + '_position',
          note: `${positionEntry.name} Position`,
          time: positionEntry.time,
          type: 'position',
          derived: true,
          showInEventReport: true,
        })
    )
)

/** @type {CaseSelector<Record<string, number>>} */
export const selectPositionTimesByEventId = createSelector(
  selectPositionEvents,
  events => {
    /** @type {Record<string, number>} */
    const toReturn = {}
    for (const event of events) {
      toReturn[event.id] = event.time
    }
    return toReturn
  }
)
